import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const NAVY = '#1B2A4A';
const GOLD = '#C9A84C';
const EMERALD = '#10B981';
const GRAY = '#9CA3AF';

type Period = '3m' | '6m' | '12m';

interface MonthlyData {
  month: string;
  revenus: number;
  tickets: number;
}

interface RevenueByEventItem {
  id: string;
  event: string;
  revenue: number;
  tickets: number;
  status: 'upcoming' | 'ongoing' | 'completed';
}

interface RevenueByTypeItem {
  type: string;
  revenus: number;
  fill: string;
}

interface ExpenseBreakdownItem {
  category: string;
  amount: number;
  fill: string;
}

interface FinancialDashboardProps {
  monthlyComparison: MonthlyData[];
  revenueByEvent: RevenueByEventItem[];
  revenueByType: RevenueByTypeItem[];
  expenseBreakdown: ExpenseBreakdownItem[];
  onMoveToTrash: (eventId: string) => void;
}

const fmt = (v: number) =>
  v >= 1000000 ? `${(v / 1000000).toFixed(2)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`;

const fmtFull = (v: number) => `${v.toLocaleString('fr-FR')} FCFA`;

const RADIAN = Math.PI / 180;

interface PieLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
}

const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: PieLabelProps) => {
  if (percent < 0.06) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight="bold">{`${(percent * 100).toFixed(0)}%`}</text>;
};

const statusConfig: Record<RevenueByEventItem['status'], { label: string; color: string }> = {
  upcoming: { label: 'À venir', color: 'bg-gold-100 text-gold-dark' },
  ongoing: { label: 'En cours', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Terminé', color: 'bg-gray-100 text-gray-600' },
};

export default function FinancialDashboard({
  monthlyComparison,
  revenueByEvent,
  revenueByType,
  expenseBreakdown,
  onMoveToTrash,
}: FinancialDashboardProps) {
  const [period, setPeriod] = useState<Period>('6m');
  const [showExportHint, setShowExportHint] = useState(false);

  const filteredMonths = useMemo(() => {
    const count = period === '3m' ? 3 : period === '6m' ? 6 : 12;
    return monthlyComparison.slice(-count);
  }, [monthlyComparison, period]);

  const totalRevenue = revenueByEvent.reduce((sum, event) => sum + event.revenue, 0);
  const completedRevenue = revenueByEvent
    .filter((event) => event.status === 'completed')
    .reduce((sum, event) => sum + event.revenue, 0);
  const avgPerEvent = revenueByEvent.length > 0 ? Math.round(totalRevenue / revenueByEvent.length) : 0;
  const totalTickets = revenueByEvent.reduce((sum, event) => sum + event.tickets, 0);
  const totalExpenses = expenseBreakdown.reduce((sum, item) => sum + item.amount, 0);
  const netProfit = completedRevenue - totalExpenses;

  const kpis = [
    {
      label: 'Revenus totaux',
      value: `${fmt(totalRevenue)} FCFA`,
      sub: `${revenueByEvent.length} événements`,
      subColor: 'text-gray-400',
      icon: 'ri-money-cny-circle-line',
      gradient: 'from-gold to-gold-dark',
    },
    {
      label: 'Bénéfice net',
      value: `${fmt(netProfit)} FCFA`,
      sub: totalExpenses > 0 ? `Dépenses : ${fmt(totalExpenses)} FCFA` : 'Aucune dépense suivie',
      subColor: 'text-emerald-500',
      icon: 'ri-line-chart-line',
      gradient: 'from-emerald-500 to-teal-600',
    },
    {
      label: 'Revenu moyen / événement',
      value: `${fmt(avgPerEvent)} FCFA`,
      sub: 'Basé sur les ventes confirmées',
      subColor: 'text-gray-400',
      icon: 'ri-bar-chart-grouped-line',
      gradient: 'from-navy to-navy-lighter',
    },
    {
      label: 'Tickets vendus',
      value: totalTickets.toLocaleString('fr-FR'),
      sub: 'Toutes éditions confondues',
      subColor: 'text-gray-400',
      icon: 'ri-ticket-line',
      gradient: 'from-amber-500 to-yellow-500',
    },
  ];

  const expenseData = expenseBreakdown.length > 0
    ? expenseBreakdown
    : [{ category: 'Aucune dépense suivie', amount: 1, fill: '#E5E7EB' }];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-navy">Tableau de bord financier</h2>
          <p className="text-xs text-gray-400 mt-0.5">Analyse issue des événements et tickets réellement présents en base</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-white border border-cream-dark rounded-xl p-1 gap-1">
            {(['3m', '6m', '12m'] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${period === p ? 'bg-navy text-white' : 'text-gray-500 hover:bg-cream'}`}
              >
                {p === '3m' ? '3 mois' : p === '6m' ? '6 mois' : '12 mois'}
              </button>
            ))}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowExportHint((value) => !value)}
              className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white text-xs font-semibold rounded-xl hover:bg-navy-lighter cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-download-line"></i>Exporter
            </button>
            {showExportHint && (
              <div className="absolute right-0 top-10 z-10 bg-white border border-cream-dark rounded-xl shadow-md p-3 w-44 text-xs text-gray-600 space-y-2">
                <button onClick={() => setShowExportHint(false)} className="w-full flex items-center gap-2 hover:text-navy cursor-pointer whitespace-nowrap">
                  <i className="ri-file-excel-line text-emerald-600"></i>Export CSV
                </button>
                <button onClick={() => { window.print(); setShowExportHint(false); }} className="w-full flex items-center gap-2 hover:text-navy cursor-pointer whitespace-nowrap">
                  <i className="ri-printer-line text-navy"></i>Imprimer
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl border border-cream-dark p-4">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${kpi.gradient}`}>
                <i className={`${kpi.icon} text-white text-base`}></i>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-1">{kpi.label}</p>
            <p className="text-lg font-bold text-navy leading-tight">{kpi.value}</p>
            <p className={`text-xs mt-1 ${kpi.subColor}`}>{kpi.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 bg-white rounded-xl border border-cream-dark p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-gold/20 rounded-lg">
              <i className="ri-line-chart-line text-gold-dark text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy">Évolution mensuelle</h3>
              <p className="text-xs text-gray-400">Revenus et tickets vendus</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={filteredMonths} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradActuel" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={GOLD} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={fmt} width={42} />
              <Tooltip formatter={(value: number | null, name: string) => [value !== null ? (name === 'revenus' ? fmtFull(value) : value) : '—', name === 'revenus' ? 'Revenus' : 'Tickets']} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="revenus" stroke={GOLD} strokeWidth={2.5} fill="url(#gradActuel)" name="revenus" dot={false} activeDot={{ r: 4 }} />
              <Area type="monotone" dataKey="tickets" stroke={NAVY} strokeWidth={2} fill="transparent" name="tickets" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-cream-dark p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 flex items-center justify-center bg-navy/10 rounded-lg">
              <i className="ri-pie-chart-2-line text-navy text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy">Par type d'événement</h3>
              <p className="text-xs text-gray-400">Répartition des revenus</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={165}>
            <PieChart>
              <Pie data={revenueByType} cx="50%" cy="50%" outerRadius={70} innerRadius={32} dataKey="revenus" labelLine={false} label={renderPieLabel}>
                {revenueByType.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
              </Pie>
              <Tooltip formatter={(value: number) => [fmtFull(value), 'Revenus']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {revenueByType.length === 0 && <p className="text-xs text-gray-400">Aucune vente enregistrée pour le moment.</p>}
            {revenueByType.map((item, index) => (
              <div key={`${item.type}-${index}`} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.fill }}></span>
                  <span className="text-xs text-gray-600">{item.type}</span>
                </div>
                <span className="text-xs font-bold text-navy">{fmt(item.revenus)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 bg-white rounded-xl border border-cream-dark p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg">
              <i className="ri-bar-chart-horizontal-line text-emerald-600 text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy">Revenus par événement</h3>
              <p className="text-xs text-gray-400">Classement du plus rentable</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={[...revenueByEvent].sort((a, b) => b.revenue - a.revenue)} layout="vertical" margin={{ top: 0, right: 20, left: 8, bottom: 0 }} barSize={16}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={fmt} />
              <YAxis type="category" dataKey="event" tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false} width={120} />
              <Tooltip formatter={(value: number) => [fmtFull(value), 'Revenus']} />
              <Bar dataKey="revenue" radius={[0, 6, 6, 0]} name="revenue">
                {[...revenueByEvent].sort((a, b) => b.revenue - a.revenue).map((_, index) => (
                  <Cell key={index} fill={index === 0 ? GOLD : index === 1 ? NAVY : index <= 3 ? EMERALD : GRAY} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-cream-dark p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
              <i className="ri-money-dollar-circle-line text-red-400 text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy">Dépenses</h3>
              <p className="text-xs text-gray-400">Estimations calculées depuis l'activité</p>
            </div>
          </div>
          <div className="space-y-3 mt-2">
            {expenseData.map((item, index) => {
              const pct = totalExpenses > 0 ? Math.round((item.amount / totalExpenses) * 100) : 100;
              return (
                <div key={`${item.category}-${index}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.fill }}></span>
                      <span className="text-xs text-gray-600">{item.category}</span>
                    </div>
                    <span className="text-xs font-bold text-navy">{totalExpenses > 0 ? fmt(item.amount) : '—'}</span>
                  </div>
                  <div className="w-full bg-cream-dark rounded-full h-1.5">
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: item.fill }}></div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-cream-dark flex items-center justify-between">
            <span className="text-xs text-gray-500 font-semibold">Total dépenses</span>
            <span className="text-sm font-bold text-navy">{totalExpenses > 0 ? `${fmt(totalExpenses)} FCFA` : 'Non suivi'}</span>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-xs text-emerald-600 font-semibold">Bénéfice net</span>
            <span className="text-sm font-bold text-emerald-600">{fmt(netProfit)} FCFA</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-cream-dark">
        <div className="flex items-center justify-between px-5 py-4 border-b border-cream-dark">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-gold/20 rounded-lg">
              <i className="ri-table-line text-gold-dark text-sm"></i>
            </div>
            <h3 className="text-sm font-bold text-navy">Détail financier par événement</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-cream">
                {['Événement', 'Tickets', 'Revenus bruts', 'Statut', 'Actions'].map((header) => (
                  <th key={header} className="text-left text-xs font-semibold text-gray-400 px-5 py-3 whitespace-nowrap">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...revenueByEvent].sort((a, b) => b.revenue - a.revenue).map((event) => {
                const status = statusConfig[event.status];
                return (
                  <tr key={event.event} className="border-b border-cream hover:bg-cream/40 transition-colors">
                    <td className="px-5 py-3">
                      <p className="text-sm font-semibold text-navy">{event.event}</p>
                    </td>
                    <td className="px-5 py-3 text-sm font-bold text-navy">{event.tickets.toLocaleString('fr-FR')}</td>
                    <td className="px-5 py-3 text-sm font-bold text-navy whitespace-nowrap">{fmtFull(event.revenue)}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${status.color}`}>{status.label}</span>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => onMoveToTrash(event.id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                        title="Mettre l’événement dans la corbeille"
                        aria-label={`Mettre ${event.event} dans la corbeille`}
                      >
                        <i className="ri-delete-bin-line text-base"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {revenueByEvent.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-400">
                    Aucun revenu disponible pour le moment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
